/* ==========================================================================
   Prelim Agenda — shared constants
   --------------------------------------------------------------------------
   Preset categories + named chips (colors ported from the Rebel agenda),
   time-grid geometry, and time helpers. Used by the admin builder and the
   read-only client view.
   ========================================================================== */

export type AgendaDayMode = 'date_range' | 'day_count';

export interface AgendaBlock {
  id: string;
  day_number: number;       // 1-based day column
  start_time: string;       // "HH:mm"
  end_time: string;         // "HH:mm"
  duration_minutes: number;
  name: string;
  category_key: string;
  color: string;
  description: string | null;
  sort_order: number;
}

export interface EventAgenda {
  id: string;
  event_id: string;
  organization_id: string;
  enabled: boolean;
  title: string;
  day_mode: AgendaDayMode;
  start_date: string | null;
  day_count: number;
  blocks: AgendaBlock[];
  created_at: string;
  updated_at: string;
  is_default?: boolean;
}

export interface UpsertAgendaInput {
  enabled?: boolean;
  title?: string;
  day_mode?: AgendaDayMode;
  start_date?: string | null;
  day_count?: number;
  blocks?: AgendaBlock[];
}

// ── Preset categories (colors from Rebel activity_categories) ──────────────
export interface PresetChip {
  /** Display label, becomes the block name (editable after drop). */
  name: string;
  /** Default duration in minutes. */
  duration: number;
}

export interface PresetCategory {
  key: string;
  label: string;
  color: string;
  /** Quick-add named chips for this category. */
  chips: PresetChip[];
}

export const AGENDA_CATEGORIES: PresetCategory[] = [
  {
    key: 'transportation',
    label: 'Transportation',
    color: '#6B7280',
    chips: [
      { name: 'Airport Transfer', duration: 60 },
      { name: 'Transfer', duration: 60 },
      { name: 'Scattered Arrivals', duration: 120 },
      { name: 'Registration / Check-In', duration: 90 },
      { name: 'Scattered Departures', duration: 120 },
    ],
  },
  {
    key: 'meal',
    label: 'Meal',
    color: '#F59E0B',
    chips: [
      { name: 'Breakfast', duration: 60 },
      { name: 'Breakfast at Leisure', duration: 90 },
      { name: 'Lunch', duration: 60 },
      { name: 'Coffee Break', duration: 30 },
      { name: 'Snack', duration: 15 },
    ],
  },
  {
    key: 'meeting',
    label: 'Meeting',
    color: '#10B981',
    chips: [
      { name: 'Meetings', duration: 90 },
      { name: 'Breakout Sessions', duration: 90 },
    ],
  },
  {
    key: 'learning',
    label: 'Learning Session',
    color: '#06B6D4',
    chips: [{ name: 'Learning Session', duration: 90 }],
  },
  {
    key: 'workshop',
    label: 'Speaker / Workshop',
    color: '#EC4899',
    chips: [{ name: 'Speaker / Workshop', duration: 120 }],
  },
  {
    key: 'teambuilding',
    label: 'Team Building',
    color: '#F97316',
    chips: [{ name: 'Team Building Activity', duration: 150 }],
  },
  {
    key: 'activity',
    label: 'Activity',
    color: '#3B82F6',
    chips: [
      { name: 'Activity', duration: 120 },
      { name: 'Optional Activities', duration: 120 },
    ],
  },
  {
    key: 'leisure',
    label: 'Free Time / Leisure',
    color: '#84CC16',
    chips: [
      { name: 'Leisure Time', duration: 120 },
      { name: 'Free Time', duration: 120 },
      { name: 'Leisure Time OR Optional Activities', duration: 180 },
    ],
  },
  {
    key: 'evening',
    label: 'Evening',
    color: '#8B5CF6',
    chips: [
      { name: 'Welcome Address', duration: 30 },
      { name: 'Group Dinner', duration: 120 },
      { name: 'Final Dinner', duration: 150 },
      { name: 'Dine Arounds', duration: 150 },
      { name: 'Karaoke / Games Night', duration: 120 },
    ],
  },
  {
    key: 'wellness',
    label: 'Wellness',
    color: '#14B8A6',
    chips: [{ name: 'Wellness', duration: 90 }],
  },
  {
    key: 'excursion',
    label: 'Excursion',
    color: '#EF4444',
    chips: [{ name: 'Excursion', duration: 240 }],
  },
];

export const CATEGORY_BY_KEY: Record<string, PresetCategory> = Object.fromEntries(
  AGENDA_CATEGORIES.map((c) => [c.key, c])
);

export function categoryColor(key: string): string {
  return CATEGORY_BY_KEY[key]?.color ?? '#6B7280';
}

// ── Time-grid geometry ─────────────────────────────────────────────────────
export const GRID_START_HOUR = 7;            // 07:00
export const GRID_END_HOUR = 24;             // 00:00 (midnight)
export const GRID_TOTAL_HOURS = GRID_END_HOUR - GRID_START_HOUR; // 17
export const HOUR_HEIGHT = 64;               // px per hour
export const SNAP_MINUTES = 15;
export const MIN_BLOCK_MINUTES = 15;

export const PX_PER_MINUTE = HOUR_HEIGHT / 60;

// ── Time helpers ───────────────────────────────────────────────────────────
export function timeToMinutes(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
}

export function minutesToTime(mins: number): string {
  const clamped = Math.max(0, Math.min(24 * 60, mins));
  const h = Math.floor(clamped / 60);
  const m = clamped % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

export function computeEndTime(startTime: string, durationMinutes: number): string {
  return minutesToTime(timeToMinutes(startTime) + durationMinutes);
}

export function snapMinutes(mins: number, snap: number = SNAP_MINUTES): number {
  return Math.round(mins / snap) * snap;
}

/** "07:00" → "7:00 AM" */
export function formatTime12(t: string): string {
  const [hRaw, mRaw] = t.split(':').map(Number);
  const h = hRaw % 24;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  const m = mRaw || 0;
  return m === 0 ? `${h12} ${period}` : `${h12}:${String(m).padStart(2, '0')} ${period}`;
}

/** Human duration: 90 → "1h 30m". */
export function formatDuration(mins: number): string {
  if (mins <= 0) return '';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Y offset (px) for a time within the grid. */
export function timeToOffset(t: string): number {
  return (timeToMinutes(t) - GRID_START_HOUR * 60) * PX_PER_MINUTE;
}

export function durationToHeight(mins: number): number {
  return Math.max(mins * PX_PER_MINUTE, MIN_BLOCK_MINUTES * PX_PER_MINUTE);
}

/** Add N days to a YYYY-MM-DD date string. */
export function addDays(dateStr: string, days: number): string {
  const d = new Date(`${dateStr.slice(0, 10)}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export interface DayHeader {
  dayNumber: number;
  label: string;
  sublabel: string;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Build day headers for the grid based on the day mode. */
export function buildDayHeaders(
  dayMode: AgendaDayMode,
  startDate: string | null,
  dayCount: number
): DayHeader[] {
  const headers: DayHeader[] = [];
  for (let i = 0; i < dayCount; i++) {
    if (dayMode === 'date_range' && startDate) {
      const ds = addDays(startDate, i);
      const d = new Date(`${ds}T00:00:00Z`);
      headers.push({
        dayNumber: i + 1,
        label: `Day ${i + 1}`,
        sublabel: `${WEEKDAYS[d.getUTCDay()]} ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`,
      });
    } else {
      headers.push({ dayNumber: i + 1, label: `Day ${i + 1}`, sublabel: '' });
    }
  }
  return headers;
}

export function makeBlockId(): string {
  const rnd =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
  return `blk_${rnd}`;
}
